import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, AlertTriangle, CheckCircle2, Clock, AlertOctagon } from 'lucide-react';
import { SortSelect, sortByField } from '../../components/SortSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CIDDIYET_MAP = {
  kritik: { label: 'Kritik', color: '#ef4444', bg: 'bg-red-900/20', border: 'border-red-800/30' },
  yuksek: { label: 'Yüksek', color: '#f59e0b', bg: 'bg-amber-900/20', border: 'border-amber-800/30' },
  orta: { label: 'Orta', color: '#3b82f6', bg: 'bg-blue-900/20', border: 'border-blue-800/30' },
  dusuk: { label: 'Düşük', color: '#6b7280', bg: 'bg-gray-800/30', border: 'border-gray-700/30' },
};

const DURUM_MAP = {
  acik: { label: 'Açık', color: '#ef4444', icon: AlertOctagon },
  devam_ediyor: { label: 'Devam Ediyor', color: '#f59e0b', icon: Clock },
  cozuldu: { label: 'Çözüldü', color: '#22c55e', icon: CheckCircle2 },
  kapandi: { label: 'Kapandı', color: '#6b7280', icon: CheckCircle2 },
};

const emptyForm = { baslik: '', aciklama: '', ciddiyet: 'orta', durum: 'acik', tespit_tarihi: '', cozum_tarihi: '', sorumlu: '' };

export default function ISYEksiklikTakibi({ ihaleler, onBack }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ toplam: 0, acik: 0, cozuldu: 0, kritik_acik: 0 });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterDurum, setFilterDurum] = useState('all');
  const [sortKey, setSortKey] = useState('newest');

  const fetchEksiklik = useCallback(async (ihaleId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/eksiklik/${ihaleId}`, { withCredentials: true });
      setItems(res.data.items || []);
      setStats({ toplam: res.data.toplam, acik: res.data.acik, cozuldu: res.data.cozuldu, kritik_acik: res.data.kritik_acik });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) fetchEksiklik(selectedIhale.id); }, [selectedIhale, fetchEksiklik]);

  const openCreate = () => {
    setFormData({ ...emptyForm, tespit_tarihi: new Date().toISOString().split('T')[0] });
    setEditMode(false); setSelectedId(null); setDialogOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      baslik: item.baslik || '', aciklama: item.aciklama || '',
      ciddiyet: item.ciddiyet || 'orta', durum: item.durum || 'acik',
      tespit_tarihi: item.tespit_tarihi || '', cozum_tarihi: item.cozum_tarihi || '',
      sorumlu: item.sorumlu || ''
    });
    setEditMode(true); setSelectedId(item.id); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.baslik.trim() || !formData.tespit_tarihi) { toast.error('Başlık ve tespit tarihi zorunludur'); return; }
    setSaving(true);
    try {
      if (editMode) {
        await axios.put(`${API}/isy/eksiklik/item/${selectedId}`, formData, { withCredentials: true });
        toast.success('Eksiklik güncellendi');
      } else {
        await axios.post(`${API}/isy/eksiklik/${selectedIhale.id}`, formData, { withCredentials: true });
        toast.success('Eksiklik eklendi');
      }
      setDialogOpen(false); fetchEksiklik(selectedIhale.id);
    } catch (e) { toast.error('Hata oluştu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/eksiklik/item/${deleteId}`, { withCredentials: true });
      toast.success('Eksiklik silindi'); setDeleteId(null); fetchEksiklik(selectedIhale.id);
    } catch (e) { toast.error('Silme hatası'); }
  };

  const filtered = items.filter(i => filterDurum === 'all' || i.durum === filterDurum);
  const SORT_DEFS_EKS = {
    newest: { field: 'created_at', dir: -1 }, oldest: { field: 'created_at', dir: 1 },
    ciddiyet_yuksek: { field: 'ciddiyet', dir: 1 }, baslik_az: { field: 'baslik', dir: 1 }
  };
  const sorted = sortByField(filtered, sortKey, SORT_DEFS_EKS);

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-eksiklik-takibi">
        <h2 className="text-lg font-bold text-white">Eksiklik Takibi</h2>
        <p className="text-sm text-gray-400">Eksiklik takibi yapmak istediğiniz ihaleyi seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-red-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`eksiklik-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">Aşama: {ih.mevcut_asama}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="isy-eksiklik-takibi">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">Eksiklik Takibi</p>
          </div>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="eksiklik-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Eksiklik Ekle
        </Button>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <AlertTriangle className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.toplam}</p>
            <p className="text-xs text-gray-500">Toplam</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <AlertOctagon className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-red-400">{stats.acik}</p>
            <p className="text-xs text-gray-500">Açık</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-400">{stats.cozuldu}</p>
            <p className="text-xs text-gray-500">Çözüldü</p>
          </CardContent>
        </Card>
        {stats.kritik_acik > 0 && (
          <Card className="bg-red-900/30 border-red-700/50">
            <CardContent className="pt-4 pb-3 text-center">
              <AlertOctagon className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-red-400">{stats.kritik_acik}</p>
              <p className="text-xs text-red-300">Kritik Açık</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Select value={filterDurum} onValueChange={setFilterDurum}>
          <SelectTrigger className="h-8 w-40 bg-gray-800 border-gray-700 text-white text-sm"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all" className="text-gray-300">Tüm Durumlar</SelectItem>
            {Object.entries(DURUM_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-gray-300">{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
          { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
          { value: 'ciddiyet_yuksek', label: 'Ciddiyet (Yüksek→Düşük)' }, { value: 'baslik_az', label: 'Başlık (A-Z)' }
        ]} />
      </div>

      {/* Eksiklik Listesi */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : sorted.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-12 text-center text-gray-500">
            {items.length === 0 ? (
              <>Henüz eksiklik kaydı yok. <button onClick={openCreate} className="text-blue-400 underline ml-1">İlk eksikliği ekle</button></>
            ) : 'Filtreye uygun kayıt bulunamadı'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sorted.map(item => {
            const c = CIDDIYET_MAP[item.ciddiyet] || CIDDIYET_MAP.orta;
            const d = DURUM_MAP[item.durum] || DURUM_MAP.acik;
            const DurumIcon = d.icon;
            return (
              <Card key={item.id} className={`${c.bg} ${c.border} border transition-colors`} data-testid={`eksiklik-item-${item.id}`}>
                <CardContent className="py-2.5 px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge style={{ background: c.color + '22', color: c.color, border: `1px solid ${c.color}44` }} className="text-[10px] px-1.5 py-0">{c.label}</Badge>
                        <DurumIcon className="w-3.5 h-3.5" style={{ color: d.color }} />
                        <span className="text-[10px]" style={{ color: d.color }}>{d.label}</span>
                        <span className="text-[10px] text-gray-500">{item.tespit_tarihi}</span>
                      </div>
                      <p className="text-sm text-white mt-1">{item.baslik}</p>
                      {item.aciklama && <p className="text-xs text-gray-400 mt-0.5">{item.aciklama}</p>}
                      <div className="flex items-center gap-3 mt-1">
                        {item.sorumlu && <span className="text-[10px] text-gray-500">Sorumlu: {item.sorumlu}</span>}
                        {item.cozum_tarihi && <span className="text-[10px] text-green-400">Çözüm: {item.cozum_tarihi}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(item)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(item.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editMode ? 'Eksikliği Düzenle' : 'Yeni Eksiklik'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Başlık *</Label>
              <Input value={formData.baslik} onChange={e => setFormData({...formData, baslik: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="eksiklik-form-baslik" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Ciddiyet</Label>
              <Select value={formData.ciddiyet} onValueChange={v => setFormData({...formData, ciddiyet: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {Object.entries(CIDDIYET_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-gray-300">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Durum</Label>
              <Select value={formData.durum} onValueChange={v => setFormData({...formData, durum: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {Object.entries(DURUM_MAP).map(([k, v]) => <SelectItem key={k} value={k} className="text-gray-300">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Tespit Tarihi *</Label>
              <Input type="date" value={formData.tespit_tarihi} onChange={e => setFormData({...formData, tespit_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="eksiklik-form-tarih" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Çözüm Tarihi</Label>
              <Input type="date" value={formData.cozum_tarihi} onChange={e => setFormData({...formData, cozum_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Sorumlu</Label>
              <Input value={formData.sorumlu} onChange={e => setFormData({...formData, sorumlu: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div className="col-span-2">
              <Label className="text-gray-400 text-xs">Açıklama</Label>
              <Textarea value={formData.aciklama} onChange={e => setFormData({...formData, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-sm min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="eksiklik-form-save">
              {saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eksikliği Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu eksikliği silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
